import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calendar,
  CheckCircle2,
  Edit2,
  Eye,
  EyeOff,
  ImageIcon,
  Layers,
  MousePointerClick,
  Plus,
  Sparkles,
  Star,
  Trash2,
  TrendingUp,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type {
  CampaignStatusFilter,
  CreateSponsoredCampaignInput,
  SponsoredCampaign,
  SponsoredCollectionInput,
  SponsoredPlacement,
  SponsoredTheme,
} from '@terra/api-client';
import { Badge, EmptyState, Spinner } from '@terra/ui';

import { api } from '../lib/api';

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

function campaignStatus(c: SponsoredCampaign): {
  label: string;
  tone: 'success' | 'warning' | 'neutral' | 'danger';
} {
  const now = Date.now();
  const start = c.startDate ? new Date(c.startDate).getTime() : null;
  const end = c.endDate ? new Date(c.endDate).getTime() : null;
  if (!c.isActive) return { label: 'Draft', tone: 'neutral' };
  if (end && end < now) return { label: 'Expired', tone: 'danger' };
  if (start && start > now) return { label: 'Scheduled', tone: 'warning' };
  return { label: 'Live', tone: 'success' };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatPaise(paise: number): string {
  return `₹${(paise / 100).toLocaleString('en-IN')}`;
}

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

async function uploadToCloudinary(file: File, folder: string): Promise<string> {
  const sig = await api.files.getUploadSignature({ folder });
  const fd = new FormData();
  fd.append('file', file);
  fd.append('api_key', sig.apiKey);
  fd.append('timestamp', sig.timestamp.toString());
  fd.append('signature', sig.signature);
  fd.append('folder', sig.folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
    method: 'POST',
    body: fd,
  });
  if (!res.ok) throw new Error('Upload failed');
  const json = await res.json();
  return json.secure_url as string;
}

const THEMES: SponsoredTheme[] = [
  'DARK',
  'LUXURY',
  'ORGANIC',
  'FESTIVAL',
  'PREMIUM_GOLD',
  'CUSTOM',
];
const PLACEMENTS: SponsoredPlacement[] = [
  'HOMEPAGE_SHOWCASE',
  'HOMEPAGE_BANNER',
  'CATEGORY',
  'SEARCH',
  'PRODUCT',
  'VENDOR_PAID',
];

// ─────────────────────────────────────────────────────────────────────────────
//  Image uploader (reusable)
// ─────────────────────────────────────────────────────────────────────────────

function ImageUploader({
  label,
  value,
  onChange,
  required,
  folder = 'sponsored',
  aspect = 'h-10',
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  required?: boolean;
  folder?: string;
  aspect?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-ink text-[13px] font-semibold">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div
        className={`border-border/60 bg-surface-subtle/50 hover:bg-surface relative flex ${aspect} w-full items-center justify-between rounded-xl border px-3 transition-colors`}
      >
        {uploading ? (
          <div className="flex items-center gap-2 text-[12px] font-medium text-slate-500">
            <Spinner size="sm" />
            <span>Uploading…</span>
          </div>
        ) : value ? (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src={value}
                alt=""
                className="h-6 w-10 rounded border border-slate-100 object-cover"
              />
              <span className="text-[12px] font-medium text-emerald-600">Uploaded</span>
            </div>
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-slate-400 hover:text-red-500"
              title="Remove image"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <label className="flex w-full cursor-pointer items-center justify-between text-[13px] font-semibold text-slate-500 hover:text-slate-700">
            <span className="flex items-center gap-1.5">
              <Upload className="h-3.5 w-3.5" />
              Select Image
            </span>
            <input
              type="file"
              accept="image/*"
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploading(true);
                setErr('');
                try {
                  const url = await uploadToCloudinary(file, folder);
                  onChange(url);
                } catch (ex: any) {
                  setErr(ex.message || 'Upload failed');
                } finally {
                  setUploading(false);
                  e.target.value = '';
                }
              }}
            />
          </label>
        )}
      </div>
      {err && <span className="text-[10px] font-bold text-red-500">{err}</span>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Campaign Dashboard
// ─────────────────────────────────────────────────────────────────────────────

export function SponsoredDashboardPage() {
  const analytics = useQuery({
    queryKey: ['sponsored-analytics'],
    queryFn: () => api.sponsored.analytics(),
  });
  const all = useQuery({
    queryKey: ['sponsored-campaigns', 'all'],
    queryFn: () => api.sponsored.listAdmin({ status: 'all' }),
  });

  const stats = analytics.data;
  const recent = (all.data ?? []).slice(0, 5);

  return (
    <div className="space-y-6 pb-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-ink text-2xl font-bold tracking-tight">Sponsored Control Center</h1>
          <p className="text-ink-muted text-sm">
            Premium sponsored placements, scheduling and monetization at a glance.
          </p>
        </div>
        <Link
          to="/sponsored/new"
          className="bg-ink hover:bg-ink/90 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5"
        >
          <Plus className="h-4 w-4" /> Create Campaign
        </Link>
      </header>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Sparkles} label="Total Campaigns" value={stats?.totalCampaigns ?? 0} />
        <StatCard
          icon={Eye}
          label="Total Views"
          value={(stats?.totalViews ?? 0).toLocaleString('en-IN')}
        />
        <StatCard
          icon={MousePointerClick}
          label="Total Clicks"
          value={(stats?.totalClicks ?? 0).toLocaleString('en-IN')}
        />
        <StatCard icon={TrendingUp} label="Overall CTR" value={formatPercent(stats?.ctr ?? 0)} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm">
          <h3 className="text-ink mb-3 text-[13px] font-bold">Top Performers</h3>
          {(stats?.topCampaigns ?? []).length === 0 ? (
            <p className="text-ink-muted text-[12px]">No campaign data yet.</p>
          ) : (
            <ul className="space-y-2">
              {(stats?.topCampaigns ?? []).map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0"
                >
                  <div>
                    <p className="text-ink text-[13px] font-semibold">{c.title}</p>
                    <p className="text-ink-muted text-[11px]">{c.sponsorName}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-ink text-[12px] font-bold">{c.totalClicks} clicks</p>
                    <p className="text-ink-muted text-[10px]">CTR {formatPercent(c.ctr)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-ink text-[13px] font-bold">Recent Campaigns</h3>
            <Link to="/sponsored/active" className="text-ink-muted text-[12px] hover:underline">
              View all →
            </Link>
          </div>
          {recent.length === 0 ? (
            <EmptyState
              title="No campaigns yet"
              description="Create your first sponsored campaign to get started."
            />
          ) : (
            <CampaignTable campaigns={recent} compact />
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string | number;
}) {
  return (
    <div className="border-border/60 bg-surface rounded-2xl border p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="text-ink-muted h-4 w-4" />
        <span className="text-ink-muted text-[11px] font-bold uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="text-ink text-2xl font-bold">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Campaign list pages (filtered by status)
// ─────────────────────────────────────────────────────────────────────────────

function CampaignListPage({ status, title }: { status: CampaignStatusFilter; title: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['sponsored-campaigns', status],
    queryFn: () => api.sponsored.listAdmin({ status }),
  });

  return (
    <div className="space-y-6 pb-8">
      <header className="flex items-center justify-between">
        <h1 className="text-ink text-2xl font-bold tracking-tight">{title}</h1>
        <Link
          to="/sponsored/new"
          className="bg-ink hover:bg-ink/90 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-bold text-white shadow-sm"
        >
          <Plus className="h-4 w-4" /> New Campaign
        </Link>
      </header>

      {isLoading ? (
        <div className="flex flex-col items-center gap-3 py-12">
          <Spinner size="md" />
          <span className="text-ink-muted text-[13px]">Loading campaigns…</span>
        </div>
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          title={`No ${title.toLowerCase()}`}
          description="Once campaigns hit this status they'll appear here."
        />
      ) : (
        <CampaignTable campaigns={data ?? []} />
      )}
    </div>
  );
}

export const SponsoredActiveCampaignsPage = () => (
  <CampaignListPage status="active" title="Active Campaigns" />
);
export const SponsoredDraftCampaignsPage = () => (
  <CampaignListPage status="draft" title="Draft Campaigns" />
);
export const SponsoredScheduledCampaignsPage = () => (
  <CampaignListPage status="scheduled" title="Scheduled Campaigns" />
);
export const SponsoredExpiredCampaignsPage = () => (
  <CampaignListPage status="expired" title="Expired Campaigns" />
);

function CampaignTable({
  campaigns,
  compact = false,
}: {
  campaigns: SponsoredCampaign[];
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.sponsored.update(id, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsored-campaigns'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.sponsored.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sponsored-campaigns'] }),
  });

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
              <th className="px-5 py-3.5">Campaign</th>
              <th className="px-5 py-3.5">Status</th>
              <th className="px-5 py-3.5">Priority</th>
              {!compact && <th className="px-5 py-3.5">Schedule</th>}
              <th className="px-5 py-3.5">Views</th>
              <th className="px-5 py-3.5">Clicks</th>
              <th className="px-5 py-3.5">CTR</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c, i) => {
              const st = campaignStatus(c);
              const ctr = c.totalViews > 0 ? c.totalClicks / c.totalViews : 0;
              return (
                <motion.tr
                  key={c.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="border-border/60 hover:bg-surface-subtle/30 border-b last:border-0"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={c.heroImage}
                        alt={c.title}
                        className="h-10 w-14 rounded-lg border border-slate-100 object-cover"
                      />
                      <div>
                        <div className="text-ink flex items-center gap-2 text-[13px] font-bold">
                          {c.title}
                          {c.isFeatured && (
                            <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                          )}
                        </div>
                        <div className="text-ink-muted text-[11px]">{c.sponsorName}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <Badge tone={st.tone} className="px-2 py-0.5 text-[10px] font-bold">
                      {st.label}
                    </Badge>
                  </td>
                  <td className="text-ink-muted px-5 py-4 text-[12px] font-medium">{c.priority}</td>
                  {!compact && (
                    <td className="text-ink-muted px-5 py-4 text-[11px]">
                      <div>{formatDate(c.startDate)}</div>
                      <div className="text-ink-subtle">→ {formatDate(c.endDate)}</div>
                    </td>
                  )}
                  <td className="text-ink-muted px-5 py-4 text-[12px] font-medium">
                    {c.totalViews}
                  </td>
                  <td className="text-ink-muted px-5 py-4 text-[12px] font-medium">
                    {c.totalClicks}
                  </td>
                  <td className="text-ink-muted px-5 py-4 text-[12px] font-medium">
                    {formatPercent(ctr)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        title={c.isActive ? 'Pause' : 'Activate'}
                        onClick={() => toggle.mutate({ id: c.id, isActive: !c.isActive })}
                        className="text-ink-subtle hover:text-ink hover:bg-surface-subtle rounded p-1.5"
                      >
                        {c.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <Link
                        to={`/sponsored/edit/${c.id}`}
                        className="text-ink-subtle hover:text-ink hover:bg-surface-subtle rounded p-1.5"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Link>
                      <button
                        title="Delete"
                        onClick={() => {
                          if (window.confirm(`Delete campaign "${c.title}"?`)) remove.mutate(c.id);
                        }}
                        className="text-ink-subtle rounded p-1.5 hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Create / Edit Campaign
// ─────────────────────────────────────────────────────────────────────────────

interface FormState {
  title: string;
  subtitle: string;
  sponsorName: string;
  heroImage: string;
  ctaText: string;
  ctaUrl: string;
  couponCode: string;
  offerLabel: string;
  backgroundTheme: SponsoredTheme;
  isActive: boolean;
  isFeatured: boolean;
  startDate: string;
  endDate: string;
  /** Up to 3 collection thumbnails shown inside the sponsored showcase. */
  collectionImages: Array<{ id?: string; thumbnailImage: string }>;
}

const EMPTY_FORM: FormState = {
  title: '',
  subtitle: '',
  sponsorName: '',
  heroImage: '',
  ctaText: 'Shop Now',
  ctaUrl: '/',
  couponCode: '',
  offerLabel: '',
  backgroundTheme: 'PREMIUM_GOLD',
  isActive: true,
  isFeatured: false,
  startDate: '',
  endDate: '',
  collectionImages: [],
};

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIsoFromLocal(local: string): string | undefined {
  if (!local) return undefined;
  return new Date(local).toISOString();
}

export function SponsoredCampaignFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const existing = useQuery({
    queryKey: ['sponsored-campaign', id],
    queryFn: () => api.sponsored.get(id!),
    enabled: isEdit,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    if (existing.data) {
      const c = existing.data;
      setForm({
        title: c.title,
        subtitle: c.subtitle ?? '',
        sponsorName: c.sponsorName,
        heroImage: c.heroImage,
        ctaText: c.ctaText,
        ctaUrl: c.ctaUrl,
        couponCode: c.couponCode ?? '',
        offerLabel: c.offerLabel ?? '',
        backgroundTheme: (c.backgroundTheme as SponsoredTheme) ?? 'PREMIUM_GOLD',
        isActive: c.isActive,
        isFeatured: c.isFeatured,
        startDate: toLocalInput(c.startDate),
        endDate: toLocalInput(c.endDate),
        collectionImages: (c.collections ?? []).map((col) => ({
          id: col.id,
          thumbnailImage: col.thumbnailImage,
        })),
      });
    }
  }, [existing.data]);

  const buildPayload = (): CreateSponsoredCampaignInput => {
    const collections: SponsoredCollectionInput[] = form.collectionImages
      .filter((c) => c.thumbnailImage.trim().length > 0)
      .map((c, idx) => ({
        ...(c.id ? { id: c.id } : {}),
        collectionTitle: `${form.sponsorName.trim() || 'Collection'} ${idx + 1}`,
        thumbnailImage: c.thumbnailImage,
        redirectUrl: form.ctaUrl.trim() || '/',
        sortOrder: idx,
        isActive: true,
      }));
    const payload: CreateSponsoredCampaignInput = {
      title: form.title.trim(),
      sponsorName: form.sponsorName.trim(),
      heroImage: form.heroImage,
      ctaText: form.ctaText.trim() || 'Shop Now',
      ctaUrl: form.ctaUrl.trim() || '/',
      backgroundTheme: form.backgroundTheme,
      placement: 'HOMEPAGE_SHOWCASE',
      isActive: form.isActive,
      isFeatured: form.isFeatured,
      priority: form.isFeatured ? 10 : 100,
      collections,
    };
    if (form.subtitle.trim()) payload.subtitle = form.subtitle.trim();
    if (form.couponCode.trim()) payload.couponCode = form.couponCode.trim();
    if (form.offerLabel.trim()) payload.offerLabel = form.offerLabel.trim();
    const startIso = toIsoFromLocal(form.startDate);
    if (startIso) payload.startDate = startIso;
    const endIso = toIsoFromLocal(form.endDate);
    if (endIso) payload.endDate = endIso;
    return payload;
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = buildPayload();
      if (isEdit) return api.sponsored.update(id!, body);
      return api.sponsored.create(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sponsored-campaigns'] });
      void qc.invalidateQueries({ queryKey: ['sponsored-analytics'] });
      navigate('/sponsored/active');
    },
  });

  const addCollectionImage = () => {
    setForm((f) => ({
      ...f,
      collectionImages: [...f.collectionImages, { thumbnailImage: '' }],
    }));
  };

  const updateCollectionImage = (idx: number, thumbnailImage: string) => {
    setForm((f) => ({
      ...f,
      collectionImages: f.collectionImages.map((c, i) =>
        i === idx ? { ...c, thumbnailImage } : c,
      ),
    }));
  };

  const removeCollectionImage = (idx: number) => {
    setForm((f) => ({
      ...f,
      collectionImages: f.collectionImages.filter((_, i) => i !== idx),
    }));
  };

  if (isEdit && existing.isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Spinner size="md" />
      </div>
    );
  }

  const canSave =
    form.title.trim().length >= 2 &&
    form.sponsorName.trim().length >= 2 &&
    form.heroImage.length > 0;

  return (
    <form
      className="space-y-6 pb-12"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) save.mutate();
      }}
    >
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-ink text-2xl font-bold tracking-tight">
            {isEdit ? 'Edit Campaign' : 'Create Sponsored Campaign'}
          </h1>
          <p className="text-ink-muted text-sm">
            Configure media, schedule, theme and store collections shown in the sponsored showcase.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/sponsored/active"
            className="text-ink-muted hover:text-ink rounded-xl px-3 py-2 text-[13px] font-semibold"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={!canSave || save.isPending}
            className="bg-ink hover:bg-ink/90 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-bold text-white shadow-sm disabled:opacity-50"
          >
            {save.isPending ? <Spinner size="sm" /> : <CheckCircle2 className="h-4 w-4" />}
            {isEdit ? 'Save changes' : 'Publish campaign'}
          </button>
        </div>
      </header>

      <Section title="Sponsor & Title" icon={Sparkles}>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Sponsor / Store Name" required>
            <input
              className={inputCls}
              value={form.sponsorName}
              onChange={(e) => setForm((f) => ({ ...f, sponsorName: e.target.value }))}
              placeholder="Gharghuti Spices"
              required
            />
          </Field>
          <Field label="Headline Title" required>
            <input
              className={inputCls}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Aromatic Pure Spices"
              required
            />
          </Field>
          <Field label="Subtitle / Description">
            <input
              className={inputCls}
              value={form.subtitle}
              onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
              placeholder="Hand-pounded premium masalas, 100% natural"
            />
          </Field>
          <Field label="Theme (background + accent)">
            <select
              className={inputCls}
              value={form.backgroundTheme}
              onChange={(e) =>
                setForm((f) => ({ ...f, backgroundTheme: e.target.value as SponsoredTheme }))
              }
            >
              {THEMES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Hero Image" icon={ImageIcon}>
        <div className="grid gap-5 sm:grid-cols-2">
          <ImageUploader
            label="Hero Image (the big banner in the showcase)"
            value={form.heroImage}
            onChange={(url) => setForm((f) => ({ ...f, heroImage: url }))}
            required
          />
        </div>
      </Section>

      <Section title="Offer & CTA" icon={MousePointerClick}>
        <div className="grid gap-5 sm:grid-cols-4">
          <Field label="Offer Label">
            <input
              className={inputCls}
              value={form.offerLabel}
              onChange={(e) => setForm((f) => ({ ...f, offerLabel: e.target.value }))}
              placeholder="Min. 30% Off"
            />
          </Field>
          <Field label="Coupon Code">
            <input
              className={inputCls}
              value={form.couponCode}
              onChange={(e) => setForm((f) => ({ ...f, couponCode: e.target.value }))}
              placeholder="SPICES15"
            />
          </Field>
          <Field label="CTA Text">
            <input
              className={inputCls}
              value={form.ctaText}
              onChange={(e) => setForm((f) => ({ ...f, ctaText: e.target.value }))}
              placeholder="Shop Now"
            />
          </Field>
          <Field label="CTA Link">
            <input
              className={inputCls}
              value={form.ctaUrl}
              onChange={(e) => setForm((f) => ({ ...f, ctaUrl: e.target.value }))}
              placeholder="/search?q=spices"
            />
          </Field>
        </div>
      </Section>

      <Section title="Schedule & Visibility" icon={Calendar}>
        <div className="grid gap-5 sm:grid-cols-4">
          <Field label="Start Date (optional)">
            <input
              type="datetime-local"
              className={inputCls}
              value={form.startDate}
              onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
            />
          </Field>
          <Field label="End Date (optional)">
            <input
              type="datetime-local"
              className={inputCls}
              value={form.endDate}
              onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
            />
          </Field>
          <Field label="Active">
            <ToggleRow
              checked={form.isActive}
              onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
              label="Visible on storefront"
            />
          </Field>
          <Field label="Featured">
            <ToggleRow
              checked={form.isFeatured}
              onChange={(v) => setForm((f) => ({ ...f, isFeatured: v }))}
              label="Show first in showcase"
            />
          </Field>
        </div>
      </Section>

      <Section title="Store Collection Thumbnails" icon={Layers}>
        <div className="space-y-3">
          <p className="text-ink-muted text-[12px]">
            Up to 3 product images shown inside the showcase grid for this campaign.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {form.collectionImages.map((col, idx) => (
              <div key={idx} className="relative">
                <ImageUploader
                  label={`Thumbnail ${idx + 1}`}
                  value={col.thumbnailImage}
                  onChange={(url) => updateCollectionImage(idx, url)}
                  folder="sponsored/collections"
                />
                <button
                  type="button"
                  onClick={() => removeCollectionImage(idx)}
                  className="text-ink-subtle absolute -right-2 -top-2 rounded-full bg-white p-1 shadow-sm hover:bg-red-50 hover:text-red-500"
                  title="Remove"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          {form.collectionImages.length < 3 && (
            <button
              type="button"
              onClick={addCollectionImage}
              className="border-border/60 text-ink hover:bg-surface-subtle inline-flex items-center gap-2 rounded-xl border border-dashed px-3 py-2 text-[13px] font-semibold"
            >
              <Plus className="h-4 w-4" /> Add thumbnail
            </button>
          )}
        </div>
      </Section>

      {save.isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-[12px] font-semibold text-red-700">
          Save failed: {save.error?.message ?? 'Unknown error'}
        </div>
      )}
    </form>
  );
}

const inputCls =
  'border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 placeholder:text-ink-subtle h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2';

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-ink text-[13px] font-semibold">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) {
  return (
    <section className="border-border/60 bg-surface rounded-2xl border shadow-sm">
      <header className="border-border/60 flex items-center gap-3 border-b px-5 py-4">
        <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
          <Icon className="h-4 w-4" />
        </div>
        <h2 className="text-ink text-[14px] font-bold">{title}</h2>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex h-10 cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-white px-3">
      <span className="text-ink text-[13px] font-medium">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-slate-900"
      />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Analytics page
// ─────────────────────────────────────────────────────────────────────────────

export function SponsoredAnalyticsPage() {
  const analytics = useQuery({
    queryKey: ['sponsored-analytics'],
    queryFn: () => api.sponsored.analytics(),
  });
  const stats = analytics.data;

  return (
    <div className="space-y-6 pb-8">
      <header>
        <h1 className="text-ink text-2xl font-bold tracking-tight">Sponsored Analytics</h1>
        <p className="text-ink-muted text-sm">
          Performance, click-through and revenue across campaigns.
        </p>
      </header>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={Eye}
          label="Impressions"
          value={(stats?.totalViews ?? 0).toLocaleString('en-IN')}
        />
        <StatCard
          icon={MousePointerClick}
          label="Clicks"
          value={(stats?.totalClicks ?? 0).toLocaleString('en-IN')}
        />
        <StatCard icon={TrendingUp} label="CTR" value={formatPercent(stats?.ctr ?? 0)} />
        <StatCard icon={BarChart3} label="Revenue" value={formatPaise(stats?.revenuePaise ?? 0)} />
      </div>

      <div className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm">
        <h3 className="text-ink mb-4 text-[14px] font-bold">Top Campaigns</h3>
        {(stats?.topCampaigns ?? []).length === 0 ? (
          <EmptyState
            title="No data yet"
            description="Once campaigns start receiving impressions, top performers appear here."
          />
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-border/60 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
                <th className="py-2.5">Campaign</th>
                <th className="py-2.5">Views</th>
                <th className="py-2.5">Clicks</th>
                <th className="py-2.5">CTR</th>
                <th className="py-2.5">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.topCampaigns ?? []).map((c) => (
                <tr key={c.id} className="border-border/60 border-b last:border-0">
                  <td className="py-3">
                    <p className="text-ink text-[13px] font-semibold">{c.title}</p>
                    <p className="text-ink-muted text-[11px]">{c.sponsorName}</p>
                  </td>
                  <td className="text-ink-muted py-3 text-[12px]">{c.totalViews}</td>
                  <td className="text-ink-muted py-3 text-[12px]">{c.totalClicks}</td>
                  <td className="text-ink-muted py-3 text-[12px]">{formatPercent(c.ctr)}</td>
                  <td className="text-ink-muted py-3 text-[12px]">{formatPaise(c.revenuePaise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Placement Management
// ─────────────────────────────────────────────────────────────────────────────

export function SponsoredPlacementsPage() {
  const all = useQuery({
    queryKey: ['sponsored-campaigns', 'all'],
    queryFn: () => api.sponsored.listAdmin({ status: 'all', limit: 100 }),
  });

  const byPlacement = useMemo(() => {
    const map = new Map<SponsoredPlacement, SponsoredCampaign[]>();
    (all.data ?? []).forEach((c) => {
      const list = map.get(c.placement) ?? [];
      list.push(c);
      map.set(c.placement, list);
    });
    return map;
  }, [all.data]);

  return (
    <div className="space-y-6 pb-8">
      <header>
        <h1 className="text-ink text-2xl font-bold tracking-tight">Placement Management</h1>
        <p className="text-ink-muted text-sm">
          Campaigns grouped by where they appear on the storefront. Future placements (search,
          category, vendor-paid) plug in here without code changes.
        </p>
      </header>

      <div className="space-y-4">
        {PLACEMENTS.map((p) => {
          const items = byPlacement.get(p) ?? [];
          return (
            <div key={p} className="border-border/60 bg-surface rounded-2xl border shadow-sm">
              <header className="border-border/60 flex items-center justify-between border-b px-5 py-4">
                <div>
                  <h3 className="text-ink text-[14px] font-bold">{p.replaceAll('_', ' ')}</h3>
                  <p className="text-ink-muted text-[11px]">{items.length} campaign(s)</p>
                </div>
                <Link
                  to="/sponsored/new"
                  className="text-ink-muted hover:text-ink inline-flex items-center gap-1 text-[12px] font-semibold"
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </Link>
              </header>
              {items.length === 0 ? (
                <p className="text-ink-muted px-5 py-6 text-[12px]">
                  No campaigns in this placement.
                </p>
              ) : (
                <CampaignTable campaigns={items} compact />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
